import React from 'react';
import {
    CLIENT_PROFILE_DOCUMENT_FILES_PATH,
    ClientDocument,
    ClientDocumentColumn,
    ClientDocumentSortBy,
    ClientDocumentTypeMap,
    SortOrder
} from "./domain";
import {SimpleDropdown, ErrorDisplay} from "common";
import {CaretDownIcon, CaretUpIcon, Icon, Link, Pane} from "evergreen-ui";
import {gql, useQuery} from "@apollo/client";
import {LoadingSpinner, Paths, RelayConnection} from "../../common";
import {RelayPagination, RelayPaginationState, useRelayPagination} from "../pagination";
import {ClientDocumentListAction} from "./clientDocumentReducer";
import {NavigationSearchBar} from "../../placement/components/NavigationSearchBar";
import {get} from "lodash";
import {formatDateStr} from "../../common/util/date";
import {useLocation} from "react-router";


const DocumentFileListColumns: Readonly<ClientDocumentColumn[]> = [
    {name: 'Document Name', attr: 'documentName'},
    {name: 'Type', attr: 'documentType'},
    {name: 'Description', attr: 'documentDescription'},
    {name: 'Notification Date', attr: 'documentDate'},
]

export const CLIENT_DOCUMENT_LIST = gql`
query clientDocumentQuery($first: Int = 20, $after: String, $clientGuid: String, $params: ClientDocumentQueryParamsInput!, $search: String) {
    clientDocumentQuery(
        first: $first
        after: $after
        clientGuid: $clientGuid
        params: $params
        search: $search
    ) {
        edges {
            cursor
            node {
                guid 
                documentName
                documentType
                documentDescription
                documentDate
                documentLink
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }
  } 
`

export type ClientDocumentQueryResponse = RelayConnection<ClientDocument> & { totalElements: number };

type ClientDocumentListParams = {
    sortBy: ClientDocumentSortBy;
    documentTypeFilter: string;
    pagination: RelayPaginationState;
    dispatch: React.Dispatch<ClientDocumentListAction>;
    searchText: string;
    clientGuid: string
}

export function ClientDocumentList({
                                       pagination,
                                       searchText,
                                       sortBy,
                                       documentTypeFilter,
                                       clientGuid,
                                       dispatch
                                   }: ClientDocumentListParams) {
    const location = useLocation();

    const {loading, data, error} = useQuery<{ clientDocumentQuery: ClientDocumentQueryResponse }, {}>(
        CLIENT_DOCUMENT_LIST, {
            skip: !location.pathname.includes(CLIENT_PROFILE_DOCUMENT_FILES_PATH),
            variables: {
                ...pagination.toGraphQlParams(),
                clientGuid: clientGuid,
                search: searchText === "" ? null : searchText,
                params: {
                    documentType: documentTypeFilter === "" ? null : documentTypeFilter,
                    sortDir: sortBy.order,
                    sortBy: sortBy.column,
                },
            }
        }
    )

    const setSortByColumn = (newSortColumn: Paths<ClientDocument>) => {
        // ignore sort by service types
        let newSortOrder = SortOrder.DESC;
        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn, order: newSortOrder}});
    }

    const displayCol = (document: ClientDocument, attr: Paths<ClientDocument>) => {
        const value = get(document, attr);
        if (attr === "documentType" && value) {
            return ClientDocumentTypeMap.get(value);
        } else if (attr === "documentDate" && value) {
            return formatDateStr(value);
        }

        return value;
    }

    let totalDocuments: number = 0;
    if (data?.clientDocumentQuery?.pageInfo?.endCursor !== null) {
        totalDocuments = data?.clientDocumentQuery?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalDocuments);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading client documents" />;
    }

    return (
        <div className="h-full relative">
            <div className="h-full p-5 bg-white shadow-md scrollbar-always">
                <div className="flex flex-col w-full relative">
                    <div className="flex items-center justify-between">
                        <div className="text-atlantis-200">
                            <SimpleDropdown
                                options={ClientDocumentTypeMap}
                                selected={documentTypeFilter}
                                minWidth={"7rem"}
                                textColor={"atlantis-200"}
                                setSelected={(selected) => {
                                    dispatch({type: 'SET_DOCUMENT_TYPE_FILTER', documentTypeFilter: selected})
                                }}/>
                        </div>
                        <NavigationSearchBar placeholder={"Search by Name or Description"}
                                             searchQuery={searchText}
                                             setSearchQuery={(search) => dispatch({
                                                 type: 'SET_SEARCH',
                                                 search: search
                                             })}/>
                    </div>

                    <Pane display='flex' justifyContent='flex-end' padding={20}>
                        <RelayPagination pageInfo={data?.clientDocumentQuery.pageInfo} count={totalDocuments}
                                         onClick={onPaginationClick}/>
                    </Pane>
                    <table className='table-auto w-full'>
                        <thead>
                        <tr className='text-left select-none text-xs text-gray-portal-table-title'>
                            {
                                DocumentFileListColumns.map((column) => (
                                    <th
                                        key={column.attr}
                                        className={'font-light' + (column.attr === 'documentName' ? ' pl-4' : '')}
                                        onClick={() => setSortByColumn(column.attr)}
                                    >
                                        <div className='flex items-center'>
                                            <div>
                                                {column.name}
                                            </div>
                                            <div className='w-6 h-6 pl-2'>
                                                {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                    <Icon className='align-middle' icon={CaretUpIcon}/>}
                                                {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                    <Icon className='align-middle' icon={CaretDownIcon}/>}
                                            </div>
                                        </div>
                                    </th>
                                ))
                            }
                        </tr>
                        </thead>
                        <tbody>
                        {
                            !loading && data!.clientDocumentQuery.edges.map((edge) => edge.node).map((file) =>
                                <tr key={file.guid}
                                    className="border-b text-sm border-gray-200 hover:bg-botticelli-100">
                                    {
                                        DocumentFileListColumns.map((column) => (
                                            <td
                                                key={column.name}
                                                className={'py-5' + (column.attr === 'documentName' ? ' pl-4 text-blue-portal-link underline' : '')}
                                            >
                                                {
                                                    column.attr === 'documentName' ?
                                                        <Link target="_blank" rel="noopener" href={file.documentLink}>
                                    <span className="text-blue-portal-link">
                                        {file.documentName}
                                    </span>
                                                        </Link> : displayCol(file, column.attr)
                                                }
                                            </td>
                                        ))
                                    }
                                </tr>)
                        }
                        </tbody>
                    </table>
                    {
                        loading && <LoadingSpinner spacing={20} height={"60vh"}/>
                    }
                    {
                        totalDocuments === 0 && !loading && <div className="text-center p-8 w-full">
                            No documents matched the searching criteria.
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}
